import { dayjs } from "@thalamos/common";
import type { ExternalPatientLinkEnhanced } from "@thalamos/aspire-common";

import { getBadgeColor } from "../ManualPullButton/helpers.js";

type ExternalPatientLink = Pick<
  ExternalPatientLinkEnhanced,
  | "id"
  | "patientId"
  | "externalSystemType"
  | "externalSystemId"
  | "externalSystemDisplayName"
  | "externalPatientIdType"
  | "externalPatientIdValue"
  | "dateLastProcessed"
  | "priorityWithinExternalSystem"
  | "canPull"
  | "canPush"
  | "isInvalid"
>;

/**
 * Filters external links down to those that are visible and pullable, applying
 * the Rio-wins deduplication rule when both Rio and PDS links are present.
 *
 * This is the single source of truth for the filtering logic used by both
 * `PatientExternalLinkActions` and `PatientBanner` (for the mobile badge colour).
 */
export function filterPullableLinks(
  externalLinks: ExternalPatientLink[],
  shouldShowCreateRioLinkButton: boolean,
  shouldShowLinkToPdsButton: boolean,
): ExternalPatientLink[] {
  const filtered = externalLinks.filter((link) => {
    if (link.isInvalid) return false;
    if (!link.canPull) return false;
    if (link.externalSystemType === "rio" && shouldShowCreateRioLinkButton)
      return false;
    if (link.externalSystemType === "pds" && shouldShowLinkToPdsButton)
      return false;
    return true;
  });

  // If there are both Rio and PDS links, only show the Rio link
  const rioLink = filtered.find((l) => l.externalSystemType === "rio");
  const pdsLink = filtered.find((l) => l.externalSystemType === "pds");

  if (rioLink && pdsLink) {
    return [rioLink];
  }

  // Prioritise Rio over PDS
  return filtered.sort((a, b) => {
    if (a.externalSystemType === "rio" && b.externalSystemType !== "rio")
      return -1;
    if (b.externalSystemType === "rio" && a.externalSystemType !== "rio")
      return 1;
    return 0;
  });
}

/**
 * Computes the worst-case sync badge colour from a pre-filtered list of pullable
 * external links. Returns "error" if any link is overdue (>14 days), "warning"
 * if near-due (7–14 days), or undefined if all links are within the threshold.
 *
 * Accepts already-filtered links (from `filterPullableLinks`) so the caller
 * controls filtering independently of badge computation.
 */
export function computeSyncBadgeColour(
  pullableLinks: ExternalPatientLink[],
): "error" | "warning" | undefined {
  if (pullableLinks.length === 0) return undefined;

  // A missing dateLastProcessed is treated as the most overdue possible
  if (pullableLinks.some((l) => l.dateLastProcessed === undefined)) {
    return "error";
  }

  const oldestPullable = pullableLinks.reduce((oldest, current) =>
    dayjs(current.dateLastProcessed).isBefore(dayjs(oldest.dateLastProcessed))
      ? current
      : oldest,
  );

  return getBadgeColor(oldestPullable.dateLastProcessed);
}
